import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonProps } from '@mui/material/Button';
import type { SxProps } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIconButton from '../common/CloseIconButton';

interface ConfirmationModalProps {
  open: boolean;
  handleClose: () => void;
  onConfirm: () => void;
  type: 'save' | 'delete' | 'warning';
  title?: string;
  // Default simple content API (backward compatible)
  contentMessage?: string | React.ReactNode;
  // If provided, renders this node as the entire dialog content (icon + title + message, etc.)
  customContent?: React.ReactNode;
  // Optional action controls (all optional to keep full backward compatibility)
  cancelText?: string;
  confirmText?: string;
  centerActions?: boolean; // center the buttons in the footer
  fullWidthButtons?: boolean; // make buttons take equal width
  cancelButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  confirmButtonProps?: Omit<ButtonProps, 'onClick' | 'children'>;
  actionsSx?: SxProps; // extra styling for DialogActions container
  // High-value: prevent double submit and provide visual feedback
  isConfirming?: boolean;
  // Show close button in title (default: false)
  showCloseButton?: boolean;
}
  
export function ConfirmationModal({
  open,
  handleClose,
  onConfirm,
  type,
  title = type === 'delete' ? 'Delete Confirmation' : 'Save Confirmation',
  contentMessage = type === 'delete' ? 'Do you want to delete?' : 'Are you sure that you want to save the changes?',
  customContent,
  cancelText,
  confirmText,
  centerActions,
  fullWidthButtons,
  cancelButtonProps,
  confirmButtonProps,
  actionsSx,
  isConfirming,
  showCloseButton = false,
}: ConfirmationModalProps) {
  const { t } = useTranslation('general');

  const confirmButtonStyle = {
    borderColor: type === 'delete' ? 'red' : '#505050',
    color: type === 'delete' ? 'red' : '#505050',
    '&:hover': {
      borderColor: type === 'delete' ? 'red' : '#505050',
      backgroundColor: type === 'delete' ? 'red' : '#505050',
      color: 'white', 
    },
    textTransform: 'none',
  };
  
  const cancelButtonStyle = {
    borderColor: type === 'delete' ? '#505050' : 'red',
    color: type === 'delete' ? '#505050' : 'red',
    '&:hover': {
      borderColor: type === 'delete' ? '#505050' : 'red',
      backgroundColor: type === 'delete' ? '#505050' : 'red',
      color: 'white', 
    },
    textTransform: 'none',
  };
  
  const localizedTitle = title || (type === 'delete' ? t('DeleteConfirmation') : t('SaveConfirmation'));
  const localizedContent = contentMessage || (type === 'delete' ? t('DoYouWantToDelete') : t('AreYouSureSaveChanges'));
  const localizedCancel = cancelText ?? t('No');
  const localizedConfirm = confirmText ?? t('Yes');

  // Only apply internal (type-based) styles when consumer hasn't provided a color
  // and when the variant is 'outlined' (to avoid conflicting with contained styling)
  const applyCancelDefaults = (!cancelButtonProps?.color) && ((cancelButtonProps?.variant ?? 'outlined') === 'outlined');
  const applyConfirmDefaults = (!confirmButtonProps?.color) && ((confirmButtonProps?.variant ?? 'outlined') === 'outlined');

  // Only provide aria-labelledby/aria-describedby when we actually render the
  // default title/description nodes with matching IDs. When using customContent,
  // the consumer controls the content and its accessibility.
  const ariaLabelledBy = customContent ? undefined : 'confirmation-modal-title';
  const ariaDescribedBy = customContent ? undefined : 'confirmation-modal-description';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: '10px' } }}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {customContent ? (
        // Custom content layout (caller handles icon/title/message)
        <DialogContent>{customContent}</DialogContent>
      ) : (
        // Default layout (backward compatible)
        <>
          <DialogTitle id="confirmation-modal-title">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                {localizedTitle}
              </Typography>
              {showCloseButton && <CloseIconButton onClick={handleClose} />}
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText 
              id="confirmation-modal-description" 
              component="div"
              sx={{ 
                color: 'rgba(0, 0, 0, 0.87)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <TaskAltIcon sx={{ fontSize: 20, flexShrink: 0, mt: 0.25 }} />
              <span>{localizedContent}</span>
            </DialogContentText>
          </DialogContent>
        </>
      )}
      <DialogActions
        sx={{
          justifyContent: centerActions ? 'center' : 'flex-end',
          gap: 1.5,
          px: 3,
          pb: 2.5,
          pt: 1,
          ...(typeof actionsSx === 'object' && !Array.isArray(actionsSx) ? actionsSx : {}),
        }}
      >
        <Button
          onClick={handleClose}
          variant={cancelButtonProps?.variant ?? 'outlined'}
          color={cancelButtonProps?.color}
          size={cancelButtonProps?.size ?? 'medium'}
          startIcon={cancelButtonProps?.startIcon}
          endIcon={cancelButtonProps?.endIcon}
          sx={{
            borderRadius: '8px',
            minWidth: 90,
            ...(fullWidthButtons ? { flex: 1 } : {}),
            ...(applyCancelDefaults ? cancelButtonStyle : {}),
            ...(cancelButtonProps?.sx && typeof cancelButtonProps.sx === 'object' && !Array.isArray(cancelButtonProps.sx) ? (cancelButtonProps.sx as object) : {}),
          }}
          disabled={cancelButtonProps?.disabled}
        >
          {localizedCancel}
        </Button>
        <Button
          onClick={() => {
            onConfirm();
            if (!isConfirming) {
              // Keep backward compatibility: auto-close unless caller indicates confirming state
              handleClose();
            }
          }}
          variant={confirmButtonProps?.variant ?? 'outlined'}
          color={confirmButtonProps?.color}
          size={confirmButtonProps?.size ?? 'medium'}
          startIcon={confirmButtonProps?.startIcon}
          endIcon={confirmButtonProps?.endIcon}
          sx={{
            borderRadius: '8px',
            minWidth: 90,
            ...(fullWidthButtons ? { flex: 1 } : {}),
            ...(applyConfirmDefaults ? confirmButtonStyle : {}),
            ...(confirmButtonProps?.sx && typeof confirmButtonProps.sx === 'object' && !Array.isArray(confirmButtonProps.sx) ? (confirmButtonProps.sx as object) : {}),
          }}
          disabled={Boolean(confirmButtonProps?.disabled) || Boolean(isConfirming)}
          autoFocus
        >
          {isConfirming ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              {localizedConfirm}
            </>
          ) : (
            localizedConfirm
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { ChargeableConfirmationModal } from './ChargeableConfirmationModal';
export { default as DiscardConfirmationModal } from './DiscardConfirmationModal';
